import { NavLink, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useRealtime } from "../contexts/RealtimeContext";
import { StreakBadge } from "./StreakBadge";
import { LayoutGrid, Sparkles, Heart, Settings, LogOut, Compass } from "lucide-react";

const navItems = [
  { to: "/dashboard", icon: LayoutGrid, label: "Listen", testid: "nav-lists" },
  { to: "/timeline", icon: Heart, label: "Eure Geschichte", testid: "nav-timeline" },
  { to: "/inspiration", icon: Compass, label: "Inspiration", testid: "nav-inspiration" },
  { to: "/settings", icon: Settings, label: "Einstellungen", testid: "nav-settings" },
];

export const Layout = ({ children }) => {
  const { user, logout, partner } = useAuth();
  const { connected } = useRealtime();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user && user.name) ? user.name.charAt(0).toUpperCase() : "?";
  const partnerInitials = partner && partner.name ? partner.name.charAt(0).toUpperCase() : null;

  return (
    <div className="min-h-screen bg-alabaster grain">
      {/* Top header */}
      <header className="sticky top-0 z-30 bg-alabaster/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 h-16 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="brand-logo">
            <div className="w-9 h-9 rounded-full bg-ink text-alabaster flex items-center justify-center">
              <Sparkles size={16} strokeWidth={1.6} />
            </div>
            <div className="leading-tight">
              <div className="font-serif text-lg text-ink tracking-tight">Wisha</div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-text_secondary">Eure Bucketlist</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map(({ to, icon: Icon, label, testid }) => (
              <NavLink
                key={to}
                to={to}
                data-testid={testid}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
                    isActive
                      ? "bg-ink text-alabaster"
                      : "text-ink/70 hover:text-ink hover:bg-bone"
                  }`
                }
              >
                <Icon size={15} strokeWidth={1.7} />
                <span>{label}</span>
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <StreakBadge />
            <div
              className="hidden sm:flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-text_secondary"
              title={connected ? "Live verbunden" : "Verbinde …"}
              data-testid="realtime-status"
            >
              <span className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-sage" : "bg-text_tertiary animate-pulse"}`} />
              {connected ? "Live" : "Offline"}
            </div>
            <div className="hidden sm:flex items-center -space-x-2">
              <div className="w-9 h-9 rounded-full bg-sage text-white flex items-center justify-center text-sm font-medium border-2 border-alabaster" data-testid="user-avatar">
                {initials}
              </div>
              {partnerInitials && (
                <div className="w-9 h-9 rounded-full bg-terracotta text-white flex items-center justify-center text-sm font-medium border-2 border-alabaster" data-testid="partner-avatar">
                  {partnerInitials}
                </div>
              )}
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="p-2 rounded-full hover:bg-bone text-text_secondary hover:text-ink transition-colors"
              title="Abmelden"
            >
              <LogOut size={16} strokeWidth={1.7} />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile bottom nav */}
      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-30 bg-white/90 backdrop-blur-md border-t border-border px-2 pt-3 flex justify-around items-center"
        style={{ paddingBottom: "max(0.875rem, env(safe-area-inset-bottom))" }}
      >
        {navItems.map(({ to, icon: Icon, label, testid }) => (
          <NavLink
            key={to}
            to={to}
            data-testid={`mobile-${testid}`}
            className={({ isActive }) =>
              `flex flex-col items-center gap-1 px-4 py-1.5 rounded-full text-[11px] tracking-wider transition ${
                isActive ? "text-ink" : "text-text_secondary"
              }`
            }
          >
            <Icon size={22} strokeWidth={1.7} />
            <span>{label.split(" ")[0]}</span>
          </NavLink>
        ))}
      </nav>

      <main className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-16 pb-32 pt-8 md:pt-12">{children}</main>
    </div>
  );
};

export default Layout;
